import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import OrganisationStudent from '@/api/Dara/OrganisationStudent'
import { UnallocatedStudents, AllocatedStudents } from '@/api/Dara/InterviewSessions'
import Organisation from '@/api/Dara/Organisation'
import Students from '@/api/Dara/Students'
import { oneHourInMS } from '@/composable/useDatesAndTimes'
import type { IStudentFilter } from '@/interfaces/api/Dara/StudentFilter'
import type IDataResponse from '@/interfaces/api-resources/DataResponse'
import type { IStudentSendDetailsResponse } from '@/interfaces/api-resources/Dara/Student'
import { useInvalidateQueries as useInvalidateStudentQueries } from '@/composable/queries/Dara/students'
import { academicYearsKeys, studentsKeys } from '@/composable/queries/keys'
import type { TimelineTypeFilter } from '@/enums/TimelineTypeFilter'

interface SendDetailsData {
  students: number[];
}

interface SetGroupsData {
  group_id: number;
  sub_group_id?: number|null;
  student_ids: number[];
}

interface UpdateSettingsData {
  student_ids: number[];
  programme_id: number;
  is_hidden: boolean;
  programme_pause: Domain.StudentProgrammes.ValueObjects.ProgrammePause;
}

interface SetCustomPasswordData {
  student_ids: number[];
  password: string;
  password_confirmation: string;
}

export const useInvalidateQueries = () => {
  const queryClient = useQueryClient()

  const invalidateSingleStudentQuery = (studentId: number) => {
    queryClient.invalidateQueries({
      queryKey: studentsKeys.detail(ref(studentId)),
    })
  }

  const invalidateStudentsQuery = () => {
    queryClient.invalidateQueries({
      queryKey: studentsKeys.overviews(),
    })

    queryClient.invalidateQueries({
      queryKey: academicYearsKeys.allStats(),
    })
  }

  const invalidateOrganisationStudentCreateQuery = () => {
    queryClient.invalidateQueries({
      queryKey: studentsKeys.creates(),
    })
  }

  return {
    invalidateSingleStudentQuery,
    invalidateStudentsQuery,
    invalidateOrganisationStudentCreateQuery,
  }
}

export const useAssignLicenceToStudent = (studentId: number) =>
{
  const { invalidateSingleStudentQuery, invalidateStudentsQuery  } = useInvalidateQueries()

  return useMutation({
    mutationFn: (data: { formData: object }) => Students.assignLicence(studentId, data.formData),
    onSuccess: () => {
      invalidateSingleStudentQuery(studentId)
      invalidateStudentsQuery()
    },
  })
}

export const useEnableLicencedProgrammeToStudent = (studentId: number) =>
{
  const { invalidateSingleStudentQuery, invalidateStudentsQuery  } = useInvalidateQueries()

  return useMutation({
    mutationFn: (data: { formData: object }) => Students.enableLicencedProgramme(studentId, data.formData),
    onSuccess: () => {
      invalidateSingleStudentQuery(studentId)
      invalidateStudentsQuery()
    },
  })
}

export const useTimelineQuery = (
  programmeFilter: Ref<TimelineTypeFilter>,
  academicYearFilter: Ref<object>,
  studentId: Ref<number>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: studentsKeys.timelineList(studentId, programmeFilter, academicYearFilter),
    queryFn: () => Students.getTimeline(academicYearFilter.value, studentId.value, programmeFilter.value),
    enabled,
  })

export const useUpcomingInterviewsQuery = (
  studentId: Ref<number>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: studentsKeys.upcomingInterviews(studentId),
    queryFn: () => Students.getUpcomingInterviews(studentId.value),
    enabled,
  })

export const usePastInterviewsQuery = (
  studentId: Ref<number>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: studentsKeys.pastInterviews(studentId),
    queryFn: () => Students.getPastInterviews(studentId.value),
    enabled,
  })

export const useStudentQuery = (
  studentId: Ref<number>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: studentsKeys.detail(studentId),
    queryFn: () => Students.get(studentId.value),
    enabled,
  })

export const useUnallocatedStudentsQuery = (
  interviewSessionId: Ref<number>,
  enabled: Ref<boolean> = ref(true),
) =>
  useQuery({
    queryKey: studentsKeys.unallocated(interviewSessionId),
    queryFn: () => UnallocatedStudents.getUnallocatedStudents(interviewSessionId.value),
    enabled,
  })

export const useAllocatedStudentsQuery = (
  interviewSessionId: Ref<number>,
  enabled: Ref<boolean> = ref(true),
) =>
  useQuery({
    queryKey: studentsKeys.allocated(interviewSessionId),
    queryFn: () => AllocatedStudents.getAllocatedStudents(interviewSessionId.value),
    enabled,
  })

export const useUpdateStudent = (studentId: number) =>
{
  const { invalidateSingleStudentQuery, invalidateStudentsQuery  } = useInvalidateQueries()

  return useMutation({
    mutationFn: (updateData: { formData: object }) => Students.put(studentId, updateData.formData),
    onSuccess: () => {
      invalidateSingleStudentQuery(studentId)
      invalidateStudentsQuery()
    },
  })
}

export const useUpdateStudentGroupAndSubgroup = (studentId: number) =>
{
  const { invalidateSingleStudentQuery, invalidateStudentsQuery  } = useInvalidateQueries()

  return useMutation({
    mutationFn: (updateData: { formData: object }) => Students.updateGroupAndSubgroup(studentId, updateData.formData),
    onSuccess: () => {
      invalidateSingleStudentQuery(studentId)
      invalidateStudentsQuery()
    },
  })
}

export const useStudentsOverviewQuery = (
  organisationId: Ref<number>,
  filters: Ref<IStudentFilter>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: studentsKeys.overview(organisationId, filters),
    queryFn: () => OrganisationStudent.get(organisationId.value, filters.value),
    staleTime: oneHourInMS,
    enabled,
    placeholderData: previousData => previousData,
  })

export const useAllStudentIdsQuery = (
  organisationId: Ref<number>,
  filters: Ref<IStudentFilter>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: studentsKeys.allStudentIds(organisationId, filters),
    queryFn: () => Organisation.getAllStudentIds(organisationId.value, filters.value),
    staleTime: oneHourInMS,
    enabled,
  })

export const useSearchQuery = (
  organisationId: Ref<number>,
  search: Ref<string>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: studentsKeys.search(organisationId, search),
    queryFn: () => OrganisationStudent.search(organisationId.value, search.value),
    enabled,
    placeholderData: previousData => previousData,
  })

export const useOrganisationStudentCreateQuery = (
  organisationId: Ref<number>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: studentsKeys.create(organisationId),
    queryFn: () => OrganisationStudent.create(organisationId.value),
    staleTime: oneHourInMS,
    enabled,
  })

export const useSendUserDetails = (
  organisationId: number,
) => {
  const { invalidateStudentsQuery  } = useInvalidateQueries()

  return useMutation<IDataResponse<IStudentSendDetailsResponse>, Error, SendDetailsData>({
    mutationFn: (sendDetailsData: SendDetailsData) => OrganisationStudent.sendUserDetails(organisationId, sendDetailsData),
    onSuccess: () => {
      invalidateStudentsQuery()
    },
  })
}

export const useSetGroups = (
  organisationId: number,
) => {
  const { invalidateSingleStudentQuery, invalidateStudentsQuery  } = useInvalidateQueries()

  return useMutation<void, Error, SetGroupsData>({
    mutationFn: async (setGroupsData: SetGroupsData) => {
      await OrganisationStudent.setGroups(organisationId, setGroupsData)
    },
    onSuccess: (_, setGroupsData) => {

      for (const studentId of setGroupsData.student_ids) {
        invalidateSingleStudentQuery(studentId)
      }

      invalidateStudentsQuery()
    },
  })
}

export const useSetDefaultPasswordToSelectedStudents = (organisationId: number, studentId: number) =>
{
  const { invalidateSingleStudentQuery, invalidateStudentsQuery  } = useInvalidateQueries()

  return useMutation({
    mutationFn: (data: { formData: object }) => Organisation.setDefaultPasswordToSelectedStudents(organisationId, data.formData),
    onSuccess: () => {
      if(studentId) {
        invalidateSingleStudentQuery(studentId)
      }
      invalidateStudentsQuery()
    },
  })
}

export const useSetCustomPasswordToSelectedStudents = (organisationId: number) =>
{
  const { invalidateSingleStudentQuery, invalidateStudentsQuery  } = useInvalidateQueries()

  return useMutation({
    mutationFn: (data: { formData: SetCustomPasswordData }) => Organisation.setCustomPasswordToSelectedStudents(organisationId, data.formData),
    onSuccess: (_, { formData }) => {
      for (const studentId of formData.student_ids) {
        invalidateSingleStudentQuery(studentId)
      }

      invalidateStudentsQuery()
    },
  })
}

export const useUpdateStudentsAccess = (organisationId: number) => {
  const { invalidateSingleStudentQuery, invalidateStudentsQuery  } = useInvalidateStudentQueries()

  return useMutation({
    mutationFn: (data: { formData: UpdateSettingsData }) => OrganisationStudent.updateStudentsAccess(organisationId, data.formData),
    onSuccess: (_, { formData }) => {
      for (const studentId of formData.student_ids) {
        invalidateSingleStudentQuery(studentId)
      }

      invalidateStudentsQuery()
    },
  })
}
